import {
  Component,
  Input,
  OnInit,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  LucideAngularModule,
  AlertCircleIcon,
  BuildingIcon,
} from 'lucide-angular';
import { DoctorSlotsComponent } from '../doctor-slots';

interface DoctorStructure {
  id: string | number;
  name: string;
  eyoneExternalId?: string;
  eyone_external_id?: string;
  [key: string]: unknown;
}

@Component({
  selector: 'app-doctor-structure-slots',
  standalone: true,
  imports: [CommonModule, DoctorSlotsComponent, LucideAngularModule],
  templateUrl: './doctor-structure-slots.component.html',
  styleUrl: './doctor-structure-slots.component.scss',
})
export class DoctorStructureSlotsComponent implements OnInit, OnChanges {
  @Input() doctor!: any;
  /** Structure choisie pour la prise de RDV (par défaut : première structure) */
  selectedStructure: DoctorStructure | null = null;

  readonly AlertCircleIcon = AlertCircleIcon;
  readonly BuildingIcon = BuildingIcon;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['doctor'] && this.doctor) {
      const structures = this.structuresWithSlots;
      if (structures.length > 0 && !this.selectedStructure) {
        this.selectedStructure = structures[0];
      } else if (structures.length > 0) {
        const stillValid = structures.some(
          (s) =>
            s.id === this.selectedStructure?.id ||
            s.eyoneExternalId === this.selectedStructure?.eyoneExternalId,
        );
        if (!stillValid) {
          this.selectedStructure = structures[0];
        }
      } else {
        this.selectedStructure = null;
      }
    }
  }

  ngOnInit(): void {
    this.selectedStructure = this.structuresWithSlots[0];
  }

  get hasPriseDeRdvService(): boolean {
    const services =
      this.doctor?.doctorServicesAvailable ?? this.doctor?.serviceAvailables;
    return !!(
      services &&
      Array.isArray(services) &&
      services.some((service: any) => service?.name === 'Prise de Rdv')
    );
  }

  /** Structures ayant un eyoneExternalId (éligibles aux créneaux) — accepte camelCase ou snake_case */
  get structuresWithSlots(): DoctorStructure[] {
    if (!this.doctor?.structures?.length) return [];
    return this.doctor.structures.filter(
      (s: DoctorStructure & { eyone_external_id?: string }) => {
        const id = s?.eyoneExternalId ?? s?.eyone_external_id;
        return !!id;
      },
    ) as DoctorStructure[];
  }

  /** Plus d'une structure → le patient doit en choisir une avant de voir les créneaux */
  get isMultiStructure(): boolean {
    return this.structuresWithSlots.length > 1;
  }

  /** Structure à passer à doctor-slots : sélectionnée par l'utilisateur ou première par défaut */
  get structureForSlots(): DoctorStructure | null {
    if (this.selectedStructure) return this.selectedStructure;
    const structures = this.structuresWithSlots;
    return structures.length > 0 ? structures[0] : null;
  }

  /** Id Eyone de la structure (camelCase ou snake_case) */
  getStructureEyoneId(s: DoctorStructure): string | undefined {
    const withSnake = s as DoctorStructure & { eyone_external_id?: string };
    return s?.eyoneExternalId ?? withSnake?.eyone_external_id;
  }

  /** Afficher le bloc créneaux : une structure est déterminée (sélectionnée ou unique) */
  get shouldShowSlots(): boolean {
    const doctorEyone =
      this.doctor?.eyoneExternalId ?? (this.doctor as any)?.eyone_external_id;
    const structureEyone =
      this.structureForSlots &&
      this.getStructureEyoneId(this.structureForSlots);
    return !!(this.hasPriseDeRdvService && doctorEyone && structureEyone);
  }

  /** Indique si la structure est celle dont on affiche les créneaux (bouton actif = couleur primaire) */
  isStructureActive(structure: DoctorStructure): boolean {
    const active = this.structureForSlots;
    if (!active) return false;
    return (
      structure.id === active.id ||
      this.getStructureEyoneId(structure) === this.getStructureEyoneId(active)
    );
  }

  selectStructure(structure: DoctorStructure): void {
    // console.log('selectStructure', structure);
    this.selectedStructure = structure;
  }
}
